import { FlightFilterCategories } from '@pages/FlightSearchResult/components/FlightFilters/type';
import NewSortBar from '@pages/FlightSearchResult/components/NewSortBar';
import FilterBarCarousel from '@pages/FlightSearchResult/components/SortAndFilterBar/FilterBarCarousel';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams, useLocation } from 'react-router-dom';

import { clsx } from '@wego/alexis/helpers/clsx';

import Skeleton from '@components/Skeleton';

import {
  PAYMENT_METHOD_FILTER_BASED_ON_POS,
  FLIGHT_SEARCH_RESULTS_HEADER_HEIGHT,
  TEST_FEATURES_PARAM,
  MOCK_BAGGAGE_FILTER,
} from '@constants/flight';

import { useFlightContext } from '@context/FlightContext';

import AllFiltersIcon from '@icons/filter-simple-4x.svg';

import { getTranslationsState, getCurrentSiteState } from '@redux/selectors';

import { FlightSortType } from '@wegoTypes/flights/flightSortType';
import { SortOrder } from '@wegoTypes/sortOrder';

import FilterButtons from './FilterButtons';
import FilterPopup from './FilterPopup';
import FilterPopupContent from './FilterPopupContent';
import { removeSearchParams, getFilterResetGenzoObjects } from './helper';
import styles from './SortAndFilterBar.module.scss';

type SortAndFilterBarProps = {
  availablePaymentMethods: HomepageFlightsPaymentMethod[];
  genzoTrackApplyFilterActionEvent: (
    eventDataObjectType: string,
    eventDataActionType: string,
    eventDataPair: {
      [key: string]: any;
    },
  ) => void;
  genzoTrackApplyFilterSearchView: () => void;
  isAllFiltersOpen: boolean;
  onSortTypeChanged: (flightSortType: FlightSortType, sortOrder: SortOrder) => void;
  setIsAllFiltersOpen: (isAllFiltersOpen: boolean) => void;
};

const POPULAR_FILTERS = [
  FlightFilterCategories.Stops,
  FlightFilterCategories.Airlines,
  FlightFilterCategories.Times,
  FlightFilterCategories.Price,
];

const SortAndFilterBar: React.FC<SortAndFilterBarProps> = ({
  availablePaymentMethods,
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
  isAllFiltersOpen,
  onSortTypeChanged,
  setIsAllFiltersOpen,
}) => {
  const {
    paymentMethodIdsFilter,
    stopCodesFilter,
    originAirportCodesFilter,
    destinationAirportCodesFilter,
    activeLeg,
    stopoverOptionsFilter,
    stopoverAirportCodesFilter,
    providersFilter,
    providerCodesFilter,
    flightExperiencesFilter,
    departLegArrivalTimeBlocksFilter,
    departLegDepartureTimeBlocksFilter,
    returnLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    pricesFilter,
    airlineCodesFilter,
    allianceCodesFilter,
    tripsFilter,
    appliedFilters,
  } = useFlightSearchFilters();

  const { isApplyBaggageFilter, setIsApplyBaggageFilter } = useFlightContext();

  const { flightSearchFormHeight } = useFlightContext();

  const sortAndfilterBarContainerRef = useRef<HTMLDivElement>(null);

  const { tripViewModels, sponsoredAdTripViewModels } = useFlightSearchResults();

  const translations = useSelector(getTranslationsState);

  const { state } = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const isShowBaggageFilter = useMemo(() => {
    const testFeaturesSearchParam = searchParams.get(TEST_FEATURES_PARAM);

    return (
      testFeaturesSearchParam && testFeaturesSearchParam.split(',').includes(MOCK_BAGGAGE_FILTER)
    );
  }, [searchParams]);

  const { paymentMethods: paymentMethodsFromParams } = useFlightSearchPageParams();

  const currentSite = useSelector(getCurrentSiteState);

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [selectedFilterDropdown, setSelectedFilterDropdown] = useState<FlightFilterCategories>(
    FlightFilterCategories.Stops,
  );

  const [filterPopupCoords, setFilterPopupCoords] = useState({ top: 0, offset: 0 });

  const selectedStopCodesForActiveLeg =
    stopCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const [appliedFiltersInTopBar, setAppliedFiltersInTopBar] = useState<FlightFilterCategories[]>(
    [],
  );

  const [isStickySortFilterBar, setIsStickySortFilterBar] = useState<boolean>(false);

  const filtersToBeDisplayed = useMemo(
    () => [
      ...appliedFiltersInTopBar,
      ...POPULAR_FILTERS.filter((filter) => !appliedFiltersInTopBar.includes(filter)),
    ],
    [appliedFiltersInTopBar],
  );

  const handleBaggageFilterClick = () => {
    setIsApplyBaggageFilter((prev) => !prev);
  };

  const addOrRemoveAppliedFilters = (filter: FlightFilterCategories) => {
    const isFilterApplied = appliedFilters.includes(filter);

    if (filter === FlightFilterCategories.PaymentMethod) {
      const paymentMethods = availablePaymentMethods.filter(
        (paymentMethod) =>
          paymentMethodsFromParams.includes(paymentMethod.id) &&
          (
            PAYMENT_METHOD_FILTER_BASED_ON_POS[currentSite.countryCode] ||
            PAYMENT_METHOD_FILTER_BASED_ON_POS.default
          ).includes(paymentMethod.name.toLowerCase()),
      );

      if (!paymentMethods.length) {
        return;
      }
    }
    if (isFilterApplied && !appliedFiltersInTopBar.includes(filter)) {
      setAppliedFiltersInTopBar((prev) => [...prev, filter]);
    } else if (!isFilterApplied && appliedFiltersInTopBar.includes(filter)) {
      setAppliedFiltersInTopBar((prev) => prev.filter((appliedFilter) => appliedFilter !== filter));
    }
  };

  const handleDropdownButtonClick = (
    top: number,
    offset: number,
    clickedFilter: FlightFilterCategories,
  ) => {
    setIsAllFiltersOpen(false);
    if (selectedFilterDropdown === clickedFilter && isDropdownOpen) {
      setIsDropdownOpen(false);
    } else {
      setIsDropdownOpen(true);
    }
    setFilterPopupCoords({ top, offset });
    setSelectedFilterDropdown(clickedFilter);
  };

  const handleAllFilterButtonClicked = () => {
    setIsDropdownOpen(false);
    setIsAllFiltersOpen(!isAllFiltersOpen);
  };

  const handleClearIconClick = (filter: FlightFilterCategories) => {
    setIsAllFiltersOpen(false);
    setIsDropdownOpen(false);
    handleClearButtonClick(filter);
  };

  const handleClearButtonClick = (filter: FlightFilterCategories) => {
    const genzoObject = getFilterResetGenzoObjects(filter);
    genzoTrackApplyFilterActionEvent(filter, 'applied', genzoObject);

    removeSearchParams(searchParams, filter, activeLeg);

    if (filter === FlightFilterCategories.Airlines) {
      removeSearchParams(searchParams, FlightFilterCategories.Alliances, activeLeg);
    }

    setSearchParams(searchParams, { replace: true, state });
  };

  useEffect(() => {
    const handleBodyOnClick = (): void => {
      console.log('body click executed');
      setIsDropdownOpen(false);
    };

    const handleWindowResize = () => {
      setIsDropdownOpen(false);
    };

    document.body.addEventListener('click', handleBodyOnClick);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      document.body.removeEventListener('click', handleBodyOnClick);
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  //Sticky sort and filter bar
  useEffect(() => {
    const handleWindowScroll = (ev: Event) => {
      const window = ev.currentTarget as Window;

      if (
        sortAndfilterBarContainerRef.current &&
        window.scrollY >= FLIGHT_SEARCH_RESULTS_HEADER_HEIGHT &&
        sortAndfilterBarContainerRef.current.getBoundingClientRect().top <= flightSearchFormHeight
      ) {
        setIsStickySortFilterBar(true);
      } else {
        setIsStickySortFilterBar(false);
      }
    };

    window.addEventListener('scroll', handleWindowScroll);

    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [
    flightSearchFormHeight,
    sortAndfilterBarContainerRef.current,
    FLIGHT_SEARCH_RESULTS_HEADER_HEIGHT,
  ]);

  useEffect(() => {
    if (isDropdownOpen) {
      document.body.style.userSelect = 'none';

      return () => {
        document.body.style.userSelect = 'auto';
      };
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (!isDropdownOpen) {
      addOrRemoveAppliedFilters(FlightFilterCategories.Stops);
      addOrRemoveAppliedFilters(FlightFilterCategories.PaymentMethod);
      addOrRemoveAppliedFilters(FlightFilterCategories.BookingOptions);
      addOrRemoveAppliedFilters(FlightFilterCategories.BookingSites);
      addOrRemoveAppliedFilters(FlightFilterCategories.Experience);
      addOrRemoveAppliedFilters(FlightFilterCategories.Price);
      addOrRemoveAppliedFilters(FlightFilterCategories.Duration);
      addOrRemoveAppliedFilters(FlightFilterCategories.OriginAndDestination);
      addOrRemoveAppliedFilters(FlightFilterCategories.Transit);
      addOrRemoveAppliedFilters(FlightFilterCategories.Times);
      addOrRemoveAppliedFilters(FlightFilterCategories.Airlines);
    }
  }, [
    stopCodesFilter,
    destinationAirportCodesFilter,
    isDropdownOpen,
    originAirportCodesFilter,
    paymentMethodIdsFilter,
    providersFilter,
    providerCodesFilter,
    selectedStopCodesForActiveLeg,
    stopoverAirportCodesFilter,
    stopoverOptionsFilter,
    flightExperiencesFilter,
    departLegArrivalTimeBlocksFilter,
    departLegDepartureTimeBlocksFilter,
    returnLegArrivalTimeBlocksFilter,
    returnLegDepartureTimeBlocksFilter,
    pricesFilter,
    airlineCodesFilter,
    allianceCodesFilter,
    tripsFilter,
  ]);

  const isFilterAppliedInFilterPopup = useMemo(() => {
    return appliedFilters.includes(selectedFilterDropdown);
  }, [selectedFilterDropdown, appliedFilters]);

  return (
    <>
      <div
        ref={sortAndfilterBarContainerRef}
        style={isStickySortFilterBar ? { height: '50px' } : {}}
      ></div>

      <div
        className={clsx(styles.sortAndfilterBarContainer, isStickySortFilterBar && styles.sticky)}
        style={isStickySortFilterBar ? { top: `${flightSearchFormHeight}px` } : {}}
      >
        <div className={clsx(styles.sortAndfilterBar)}>
          <div className={styles.filterBar}>
            <FilterBarCarousel>
              {/* All Filters Button */}
              <button className={styles.allFilterButton} onClick={handleAllFilterButtonClicked}>
                <AllFiltersIcon className={styles.buttonIcon} />
                {translations.all_filter_button}
              </button>

              {isShowBaggageFilter && (
                <button
                  className={clsx(styles.allFilterButton, isApplyBaggageFilter && styles.isActive)}
                  onClick={handleBaggageFilterClick}
                >
                  {/* This is only for internal testing purpose and no translations are needed */}
                  {isApplyBaggageFilter ? 'Baggage Filter Applied' : 'Baggage Filter'}
                </button>
              )}

              {/* Filter Buttons */}
              {!(tripViewModels.length + sponsoredAdTripViewModels.length) ? (
                [1, 2, 3, 4].map((index) => (
                  <Skeleton key={index} width={100} height={38} borderRadius={8} />
                ))
              ) : (
                <FilterButtons
                  availablePaymentMethods={availablePaymentMethods}
                  isDropdownOpen={isDropdownOpen}
                  filtersToBeDisplayed={filtersToBeDisplayed}
                  selectedFilterDropdown={selectedFilterDropdown as FlightFilterCategories}
                  onClearButtonClick={handleClearIconClick}
                  onDropdownButtonClick={handleDropdownButtonClick}
                />
              )}
            </FilterBarCarousel>

            {isDropdownOpen && (
              <FilterPopup
                top={filterPopupCoords.top}
                offset={filterPopupCoords.offset}
                onClearButtonClick={() =>
                  handleClearButtonClick(selectedFilterDropdown as FlightFilterCategories)
                }
                isFilterApplied={isFilterAppliedInFilterPopup}
              >
                {
                  <FilterPopupContent
                    availablePaymentMethods={availablePaymentMethods}
                    genzoTrackApplyFilterActionEvent={genzoTrackApplyFilterActionEvent}
                    genzoTrackApplyFilterSearchView={genzoTrackApplyFilterSearchView}
                    selectedFilterDropdown={selectedFilterDropdown}
                  />
                }
              </FilterPopup>
            )}
          </div>

          <NewSortBar onSortTypeChanged={onSortTypeChanged} />
        </div>
      </div>
    </>
  );
};

export default SortAndFilterBar;
